import { useState, useEffect, useRef, useMemo, useCallback } from 'react';
import { useQuery } from '@tanstack/react-query';
import {
  MapContainer, TileLayer, Marker, Polyline, Popup, CircleMarker, useMap,
} from 'react-leaflet';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';
import dayjs from 'dayjs';
import clsx from 'clsx';
import {
  PlayIcon, PauseIcon, BackwardIcon, ForwardIcon, ArrowPathIcon,
  MapPinIcon, ClockIcon, ExclamationTriangleIcon, ArrowDownTrayIcon,
  ArrowsPointingOutIcon, TableCellsIcon,
} from '@heroicons/react/24/outline';
import { vehiclesApi } from '@/services/api';
import { vehicleTypeSvg } from '@/constants/vehicleTypes';

interface TrackPoint {
  t: string;
  lat: number;
  lng: number;
  speed: number;
  course: number | null;
  ignition: boolean | null;
}

interface Stop {
  startedAt: string;
  endedAt: string;
  durationMin: number;
  latitude: number;
  longitude: number;
}

const fmt = (t: string, p = 'YYYY-MM-DD HH:mm:ss') => dayjs(t).format(p);

/** Colour a leg by how fast it was driven, so the path reads at a glance. */
const speedColor = (kmh: number) =>
  kmh >= 100 ? '#dc2626' : kmh >= 60 ? '#f59e0b' : kmh >= 5 ? '#16a34a' : '#94a3b8';

// The moving marker. Rotated to the heading when the device reported one.
const playheadIcon = (vehicleType: string, course: number | null) => L.divIcon({
  className: 'track-playhead',
  html: `
    <div style="
      width: 38px; height: 38px; border-radius: 50%;
      background: #2563eb; border: 3px solid white;
      box-shadow: 0 2px 10px rgba(0,0,0,.4);
      display: flex; align-items: center; justify-content: center;
      transform: rotate(${course ?? 0}deg);
    ">
      <svg viewBox="0 0 24 24" width="26" height="26"
           style="transform: rotate(${-(course ?? 0)}deg)">
        ${vehicleTypeSvg(vehicleType, '#ffffff', '#2563eb')}
      </svg>
    </div>`,
  iconSize: [38, 38],
  iconAnchor: [19, 19],
});

/** Fits the map to the loaded path once, and follows the playhead on demand. */
function MapDriver({ bounds, follow, center }: {
  bounds: L.LatLngBoundsExpression | null;
  follow: boolean;
  center: [number, number] | null;
}) {
  const map = useMap();
  const fitted = useRef(false);

  useEffect(() => {
    if (bounds && !fitted.current) {
      fitted.current = true;
      map.fitBounds(bounds, { padding: [40, 40] });
    }
  }, [bounds, map]);

  // Re-fit when a different range is loaded.
  useEffect(() => { fitted.current = false; }, [bounds]);

  useEffect(() => {
    if (follow && center) map.panTo(center, { animate: true, duration: 0.4 });
  }, [follow, center, map]);

  return null;
}

const PRESETS: Array<{ label: string; from: () => [string, string] }> = [
  { label: 'آخر ساعة', from: () => [dayjs().subtract(1, 'hour').format('YYYY-MM-DDTHH:mm'), dayjs().format('YYYY-MM-DDTHH:mm')] },
  { label: 'آخر 6 ساعات', from: () => [dayjs().subtract(6, 'hour').format('YYYY-MM-DDTHH:mm'), dayjs().format('YYYY-MM-DDTHH:mm')] },
  { label: 'اليوم', from: () => [dayjs().startOf('day').format('YYYY-MM-DDTHH:mm'), dayjs().format('YYYY-MM-DDTHH:mm')] },
  { label: 'أمس', from: () => [dayjs().subtract(1, 'day').startOf('day').format('YYYY-MM-DDTHH:mm'), dayjs().subtract(1, 'day').endOf('day').format('YYYY-MM-DDTHH:mm')] },
  { label: 'آخر 7 أيام', from: () => [dayjs().subtract(7, 'day').format('YYYY-MM-DDTHH:mm'), dayjs().format('YYYY-MM-DDTHH:mm')] },
];

const SPEEDS = [1, 2, 4, 8, 16];

/**
 * Route review with playback.
 *
 * Pick a window, load the travelled path, then scrub or play it back. The
 * playhead advances one recorded fix at a time rather than by wall-clock, so
 * a gap in coverage shows as the marker pausing — which is the truth about
 * the data — instead of gliding smoothly across ground the vehicle was never
 * observed on.
 */
export default function TrackPlayback({ vehicleId, vehicleType, plateNumber }: {
  vehicleId: string;
  vehicleType: string;
  plateNumber: string;
}) {
  const [range, setRange] = useState<[string, string]>(() => PRESETS[2]!.from());
  const [applied, setApplied] = useState<[string, string] | null>(null);
  const [index, setIndex] = useState(0);
  const [playing, setPlaying] = useState(false);
  const [rate, setRate] = useState(4);
  const [follow, setFollow] = useState(true);
  const [showLog, setShowLog] = useState(false);
  const timer = useRef<number | null>(null);

  const trackQ = useQuery({
    queryKey: ['vehicle-track', vehicleId, applied],
    queryFn: async () => {
      const [s, e] = applied!;
      const r = await vehiclesApi.getTrack(
        vehicleId,
        dayjs(s).toISOString(),
        dayjs(e).toISOString(),
      );
      return r.data.data as { points: TrackPoint[]; stops: Stop[]; summary: any };
    },
    enabled: applied !== null,
    staleTime: 5 * 60_000,
  });

  const points = trackQ.data?.points ?? [];
  const stops = trackQ.data?.stops ?? [];
  const summary = trackQ.data?.summary;

  // A new dataset resets the playhead.
  useEffect(() => { setIndex(0); setPlaying(false); }, [trackQ.data]);

  // Advance while playing. Cleared on unmount so a backgrounded tab does not
  // keep a timer alive.
  useEffect(() => {
    if (!playing || points.length === 0) return;
    timer.current = window.setInterval(() => {
      setIndex((i) => {
        if (i >= points.length - 1) { setPlaying(false); return i; }
        return i + 1;
      });
    }, Math.max(16, 400 / rate));
    return () => { if (timer.current) { clearInterval(timer.current); timer.current = null; } };
  }, [playing, rate, points.length]);

  useEffect(() => () => { if (timer.current) clearInterval(timer.current); }, []);

  const current = points[Math.min(index, Math.max(0, points.length - 1))];

  // One polyline per leg so each can carry its own speed colour. Capped:
  // thousands of separate <Polyline> elements would stall the browser, so
  // above the cap the path is drawn as a single line.
  const legs = useMemo(() => {
    if (points.length < 2) return [];
    if (points.length > 1500) return null;
    const out: Array<{ positions: [number, number][]; color: string }> = [];
    for (let i = 1; i < points.length; i++) {
      out.push({
        positions: [[points[i - 1]!.lat, points[i - 1]!.lng], [points[i]!.lat, points[i]!.lng]],
        color: speedColor(points[i]!.speed),
      });
    }
    return out;
  }, [points]);

  const allPositions = useMemo(
    () => points.map((p) => [p.lat, p.lng] as [number, number]),
    [points],
  );
  const bounds = allPositions.length ? (allPositions as L.LatLngBoundsExpression) : null;

  const exportCsv = useCallback(() => {
    if (!points.length) return;
    const esc = (c: any) => `"${String(c ?? '').replace(/"/g, '""')}"`;
    const head = ['الوقت', 'خط العرض', 'خط الطول', 'السرعة (كم/س)', 'الاتجاه', 'التشغيل'];
    const rows = points.map((p) => [
      fmt(p.t), p.lat, p.lng, p.speed, p.course ?? '',
      p.ignition === null ? '' : p.ignition ? 'يعمل' : 'متوقف',
    ]);
    const csv = [head.map(esc).join(','), ...rows.map((r) => r.map(esc).join(','))].join('\r\n');
    const blob = new Blob(['﻿' + csv], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `track_${plateNumber}_${dayjs(applied?.[0]).format('YYYYMMDD-HHmm')}.csv`;
    document.body.appendChild(a); a.click(); document.body.removeChild(a);
    setTimeout(() => URL.revokeObjectURL(url), 1000);
  }, [points, plateNumber, applied]);

  return (
    <div className="card">
      <div className="card-header flex items-center justify-between gap-2 flex-wrap">
        <h3 className="font-semibold flex items-center gap-2">
          <MapPinIcon className="w-5 h-5 text-primary-500" />
          السجل ومراجعة خط السير
        </h3>
        {points.length > 0 && (
          <div className="flex items-center gap-2">
            <button onClick={() => setShowLog((v) => !v)}
                    className={clsx('btn btn-sm btn-outline', showLog && 'bg-primary-50 border-primary-300')}>
              <TableCellsIcon className="w-4 h-4 ml-1" />السجل
            </button>
            <button onClick={exportCsv} className="btn btn-sm btn-outline">
              <ArrowDownTrayIcon className="w-4 h-4 ml-1" />CSV
            </button>
          </div>
        )}
      </div>

      <div className="card-body space-y-4">
        {/* Range picker */}
        <div className="flex flex-wrap items-end gap-3">
          <div>
            <label className="block text-xs font-medium text-gray-600 mb-1">من</label>
            <input type="datetime-local" value={range[0]} max={range[1]} className="input"
                   onChange={(e) => setRange([e.target.value, range[1]])} />
          </div>
          <div>
            <label className="block text-xs font-medium text-gray-600 mb-1">إلى</label>
            <input type="datetime-local" value={range[1]} min={range[0]}
                   max={dayjs().format('YYYY-MM-DDTHH:mm')} className="input"
                   onChange={(e) => setRange([range[0], e.target.value])} />
          </div>
          <button onClick={() => setApplied([range[0], range[1]])}
                  disabled={trackQ.isFetching}
                  className="btn btn-primary disabled:opacity-50">
            {trackQ.isFetching
              ? <ArrowPathIcon className="w-5 h-5 animate-spin" />
              : <>عرض المسار</>}
          </button>
          <div className="flex flex-wrap gap-1.5">
            {PRESETS.map((p) => (
              <button key={p.label}
                      onClick={() => { const r = p.from(); setRange(r); setApplied(r); }}
                      className="btn btn-sm btn-outline">{p.label}</button>
            ))}
          </div>
        </div>

        {trackQ.error && (
          <div className="flex items-start gap-2 rounded-lg border border-warning-200 bg-warning-50 px-3 py-2">
            <ExclamationTriangleIcon className="w-5 h-5 text-warning-600 shrink-0 mt-0.5" />
            <p className="text-sm text-warning-800">
              {(trackQ.error as any)?.response?.data?.message || 'تعذّر تحميل المسار'}
            </p>
          </div>
        )}

        {applied && !trackQ.isFetching && points.length === 0 && !trackQ.error && (
          <div className="text-center py-8 text-gray-500 text-sm">
            لا توجد مواقع مسجّلة لهذه المركبة في الفترة المحددة
          </div>
        )}

        {points.length > 0 && summary && (
          <>
            {/* Summary */}
            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-2">
              {[
                { l: 'المسافة', v: `${summary.distanceKm} كم` },
                { l: 'مدة الفترة', v: `${summary.durationMin} د` },
                { l: 'زمن الحركة', v: `${summary.movingMin} د` },
                { l: 'أقصى سرعة', v: `${summary.maxSpeed} كم/س` },
                { l: 'متوسط السرعة', v: `${summary.avgSpeed} كم/س` },
                { l: 'عدد الوقفات', v: summary.stopCount },
              ].map((s) => (
                <div key={s.l} className="rounded-lg bg-gray-50 border border-gray-200 px-3 py-2">
                  <p className="text-[11px] text-gray-500">{s.l}</p>
                  <p className="font-semibold text-gray-900 text-sm">{s.v}</p>
                </div>
              ))}
            </div>

            {/* Map */}
            <div className="h-80 sm:h-96 rounded-lg overflow-hidden border border-gray-200">
              <MapContainer center={[points[0]!.lat, points[0]!.lng]} zoom={13}
                            style={{ height: '100%', width: '100%' }}>
                <TileLayer url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
                           attribution='&copy; OpenStreetMap' />
                <MapDriver bounds={bounds} follow={follow}
                           center={current ? [current.lat, current.lng] : null} />

                {legs
                  ? legs.map((leg, i) => (
                      <Polyline key={i} positions={leg.positions}
                                pathOptions={{ color: leg.color, weight: 4, opacity: 0.85 }} />
                    ))
                  : <Polyline positions={allPositions}
                              pathOptions={{ color: '#2563eb', weight: 4, opacity: 0.85 }} />}

                {/* Start and end of the reviewed window. */}
                <CircleMarker center={[points[0]!.lat, points[0]!.lng]} radius={7}
                              pathOptions={{ color: '#fff', weight: 2, fillColor: '#16a34a', fillOpacity: 1 }}>
                  <Popup><div dir="rtl">البداية<br />{fmt(points[0]!.t)}</div></Popup>
                </CircleMarker>
                <CircleMarker center={[points[points.length - 1]!.lat, points[points.length - 1]!.lng]} radius={7}
                              pathOptions={{ color: '#fff', weight: 2, fillColor: '#dc2626', fillOpacity: 1 }}>
                  <Popup><div dir="rtl">النهاية<br />{fmt(points[points.length - 1]!.t)}</div></Popup>
                </CircleMarker>

                {stops.map((s, i) => (
                  <CircleMarker key={i} center={[s.latitude, s.longitude]} radius={6}
                                pathOptions={{ color: '#fff', weight: 2, fillColor: '#f59e0b', fillOpacity: 0.95 }}>
                    <Popup>
                      <div dir="rtl" className="text-right">
                        <strong>وقوف {s.durationMin} دقيقة</strong><br />
                        من {fmt(s.startedAt, 'HH:mm')} إلى {fmt(s.endedAt, 'HH:mm')}
                      </div>
                    </Popup>
                  </CircleMarker>
                ))}

                {current && (
                  <Marker position={[current.lat, current.lng]}
                          icon={playheadIcon(vehicleType, current.course)} />
                )}
              </MapContainer>
            </div>

            {/* Transport */}
            <div className="space-y-2">
              <div className="flex items-center gap-2 flex-wrap">
                <button onClick={() => setIndex((i) => Math.max(0, i - 10))}
                        className="btn btn-sm btn-outline" title="للخلف">
                  <BackwardIcon className="w-5 h-5" />
                </button>
                <button
                  onClick={() => {
                    // Restart from the beginning if the run already finished.
                    if (index >= points.length - 1) setIndex(0);
                    setPlaying((p) => !p);
                  }}
                  className="btn btn-primary px-5"
                  title={playing ? 'إيقاف مؤقت' : 'تشغيل'}
                >
                  {playing ? <PauseIcon className="w-5 h-5" /> : <PlayIcon className="w-5 h-5" />}
                </button>
                <button onClick={() => setIndex((i) => Math.min(points.length - 1, i + 10))}
                        className="btn btn-sm btn-outline" title="للأمام">
                  <ForwardIcon className="w-5 h-5" />
                </button>

                <div className="flex items-center gap-1 mr-1">
                  {SPEEDS.map((r) => (
                    <button key={r} onClick={() => setRate(r)}
                            className={clsx('px-2 py-1 rounded text-xs font-medium border',
                              rate === r
                                ? 'bg-primary-500 text-white border-primary-500'
                                : 'bg-white text-gray-600 border-gray-300 hover:bg-gray-50')}>
                      ×{r}
                    </button>
                  ))}
                </div>

                <label className="flex items-center gap-1.5 text-sm text-gray-600 cursor-pointer mr-1">
                  <input type="checkbox" checked={follow} onChange={(e) => setFollow(e.target.checked)}
                         className="w-4 h-4 rounded border-gray-300 text-primary-600" />
                  <ArrowsPointingOutIcon className="w-4 h-4" />
                  متابعة المركبة
                </label>
              </div>

              <input
                type="range" min={0} max={Math.max(0, points.length - 1)} value={index}
                onChange={(e) => { setPlaying(false); setIndex(Number(e.target.value)); }}
                className="w-full accent-primary-600"
              />

              {current && (
                <div className="flex items-center justify-between gap-3 text-sm flex-wrap
                                rounded-lg bg-gray-50 border border-gray-200 px-3 py-2">
                  <span className="flex items-center gap-1.5 text-gray-700">
                    <ClockIcon className="w-4 h-4 text-gray-400" />
                    <span dir="ltr">{fmt(current.t)}</span>
                  </span>
                  <span className="font-semibold" style={{ color: speedColor(current.speed) }}>
                    {current.speed} كم/س
                  </span>
                  <span className="text-gray-500 text-xs" dir="ltr">
                    {current.lat.toFixed(5)}, {current.lng.toFixed(5)}
                  </span>
                  <span className="text-gray-500 text-xs">
                    {index + 1} / {points.length}
                  </span>
                </div>
              )}

              {summary.sampled && (
                <p className="text-xs text-gray-500">
                  عُرضت {summary.pointCount} نقطة من أصل {summary.rawPointCount} — خُففت العينة
                  للحفاظ على سلاسة العرض، والمسافة والإحصائيات محسوبة على كامل النقاط.
                </p>
              )}
            </div>

            {/* Stops */}
            {stops.length > 0 && (
              <div>
                <h4 className="font-medium text-gray-800 mb-2 text-sm">
                  الوقفات ({stops.length})
                </h4>
                <div className="max-h-40 overflow-y-auto rounded-lg border border-gray-200 divide-y divide-gray-100">
                  {stops.map((s, i) => (
                    <button key={i}
                            onClick={() => {
                              // Jump the playhead to the nearest fix in time.
                              const target = new Date(s.startedAt).getTime();
                              let best = 0, bestD = Infinity;
                              points.forEach((p, pi) => {
                                const d = Math.abs(new Date(p.t).getTime() - target);
                                if (d < bestD) { bestD = d; best = pi; }
                              });
                              setPlaying(false); setIndex(best);
                            }}
                            className="w-full text-right px-3 py-2 text-sm hover:bg-gray-50 flex items-center justify-between gap-2">
                      <span className="text-gray-700">وقوف {s.durationMin} دقيقة</span>
                      <span className="text-gray-500 text-xs" dir="ltr">
                        {fmt(s.startedAt, 'HH:mm')} → {fmt(s.endedAt, 'HH:mm')}
                      </span>
                    </button>
                  ))}
                </div>
              </div>
            )}

            {/* Point log */}
            {showLog && (
              <div className="max-h-72 overflow-auto rounded-lg border border-gray-200">
                <table className="table text-sm">
                  <thead className="sticky top-0 bg-white z-10">
                    <tr><th>#</th><th>الوقت</th><th>السرعة</th><th>الإحداثيات</th><th>التشغيل</th></tr>
                  </thead>
                  <tbody>
                    {points.map((p, i) => (
                      <tr key={i}
                          className={clsx('cursor-pointer', i === index && 'bg-primary-50')}
                          onClick={() => { setPlaying(false); setIndex(i); }}>
                        <td className="text-gray-400">{i + 1}</td>
                        <td className="whitespace-nowrap" dir="ltr">{fmt(p.t, 'MM-DD HH:mm:ss')}</td>
                        <td style={{ color: speedColor(p.speed) }}>{p.speed}</td>
                        <td className="text-xs text-gray-500 whitespace-nowrap" dir="ltr">
                          {p.lat.toFixed(5)}, {p.lng.toFixed(5)}
                        </td>
                        <td>{p.ignition === null ? '—' : p.ignition ? 'يعمل' : 'متوقف'}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
}
